// src/pages/UserHome.tsx
// ─────────────────────────────────────────────────────────────────────────────
// ARC-driven user home. The tile band is now the SDK's <PulseRecommendations/>,
// which fetches + ranks server-composed ARC tiles (GET /api/pulse/arc). The
// "Your data" section grid stays app-local: it derives highlight focus from the
// same activity proxies the app already loads.
// ─────────────────────────────────────────────────────────────────────────────

import { Link, useLocation } from "wouter";
import { PulseRecommendations, type ArcTile } from "@goodbetter/pulse-sdk";
import {
  useMe,
  useBodyMetrics,
  usePrograms,
  useGoals,
  usePeptides,
} from "@/lib/queries";
import { Card, PageHeader, Spinner } from "@/components/ui";
import { composeSections } from "@/lib/arc";

export function UserHome() {
  const me = useMe();
  const body = useBodyMetrics();
  const programs = usePrograms();
  const goals = useGoals();
  const peptides = usePeptides();
  const [, navigate] = useLocation();

  if (me.isLoading) return <Spinner label="Loading your PULSE…" />;

  const sections = composeSections({
    bodyCount: body.data?.items.length ?? 0,
    programCount: programs.data?.items.length ?? 0,
    goalCount: goals.data?.items.length ?? 0,
    peptidePlanCount: peptides.data?.plans.length ?? 0,
    peptideDoseCount: peptides.data?.doses.length ?? 0,
  });

  const greeting = me.data?.displayName || me.data?.email?.split("@")[0] || "athlete";

  return (
    <div className="space-y-8">
      <PageHeader
        title={`Welcome back, ${greeting}`}
        subtitle="Your Active Recommendation Context, composed from your latest signals."
      />

      <section aria-label="Active Recommendation Context">
        <PulseRecommendations
          max={5}
          onTileAction={(tile: ArcTile) => navigate(tile.primaryAction.deepLink)}
        />
      </section>

      <section aria-label="Your sections">
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-ls-muted">
          Your data
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {sections.map((s) => (
            <Link key={s.href} href={s.href}>
              <Card
                className={`cursor-pointer transition hover:border-ls-accent/50 ${
                  s.highlighted ? "border-ls-accent/40 bg-ls-accent/5" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-ls-fg">{s.label}</p>
                    <p className="text-xs text-ls-muted">{s.summary}</p>
                  </div>
                  {s.highlighted ? (
                    <span className="rounded-full bg-ls-accent/20 px-2 py-0.5 text-[10px] font-medium text-ls-accent">
                      focus
                    </span>
                  ) : null}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
